import {
  AUTH_LOGIN_FAILURE_EVENT_NAMES,
  EVENT_CATEGORIES,
  EVENT_NAMES
} from '#~/analytics/constants/index.js';
import type { AuthLoginFailureEventName } from '#~/analytics/constants/event-names.js';
import type { AuthJourneyStateTransport } from '#~/analytics/types/auth-journey.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import { processEventDeduplication } from '#~/analytics/utils/event-deduplication.js';
import type { AnalyticsTrackFn } from './types.js';

interface AuthClickProperties extends EventProperties {
  auth_journey_id?: string;
  source?: string;
}

// Per ENG-3960 W6: oauth_error event props. Never include tokens or secrets here.
// provider is the IdP name ('auth0'), error_type is an OAuth error code
// (e.g. 'access_denied'), error_message is the Auth0-controlled description.
// last_known_user_id enables deferred attribution (ENG-3479): logged-out
// events resolve to the last known user so the failure funnel is attributable.
interface OAuthErrorProperties extends EventProperties {
  provider?: string;
  error_type?: string;
  error_message?: string;
  last_known_user_id?: string;
  auth_journey_id?: string;
  source?: string;
}

// ENG-4416: routes every auth-failure emit (browser AND Electron IPC — see
// electron/api.ts's `auth:error` handler, which mirrors this loop because it
// has no AnalyticsTrackFn in scope) through debounce-config.ts's table before
// firing. AUTH_LOGIN_FAILED/OAUTH_ERROR are both configured NO_DEBOUNCE_CONFIG
// (enabled: false), so this is a no-op today; it exists so that table is
// load-bearing — a future regression that debounces this event class gets
// caught by the receipt spec instead of shipping unnoticed.
export const shouldEmitAuthFailureEvent = (
  name: AuthLoginFailureEventName,
  properties: EventProperties
): boolean => processEventDeduplication({ category: EVENT_CATEGORIES.AUTH, name, properties });

// ENG-4348 stage 3: single dual-emit path for the auth failure family. Every
// site that used to fire oauth_error inline now calls this instead, so the
// legacy/canonical pair can never drift apart property-wise — a drift would
// silently break the PostHog parity check that gates the legacy sunset.
// journey stays optional for the same reason as trackOAuthError: failures
// outside a tracked journey (expired session, direct URL) must still emit.
export const trackAuthLoginFailed = (
  track: AnalyticsTrackFn,
  journey: AuthJourneyStateTransport | undefined,
  properties: OAuthErrorProperties
): void => {
  const props: OAuthErrorProperties = journey
    ? { ...properties, auth_journey_id: journey.authJourneyId, source: journey.source }
    : properties;

  for (const name of AUTH_LOGIN_FAILURE_EVENT_NAMES) {
    if (shouldEmitAuthFailureEvent(name, props)) {
      track(name, EVENT_CATEGORIES.AUTH, props);
    }
  }
};

const withAuthJourneyProperties = (
  journey: AuthJourneyStateTransport,
  properties: AuthClickProperties = {}
): AuthClickProperties => ({
  ...properties,
  auth_journey_id: journey.authJourneyId,
  source: journey.source
});

export const createAuthJourneyTracker = (track: AnalyticsTrackFn) => ({
  // Shared registration entry event for browser and Electron. Electron opens
  // Auth0 directly, so relying on the browser-only /register route leaves the
  // desktop funnel without its started stage.
  trackRegistrationStarted: (
    journey: AuthJourneyStateTransport,
    properties?: AuthClickProperties
  ): void => {
    track(
      EVENT_NAMES.REGISTRATION_STARTED,
      EVENT_CATEGORIES.ONBOARDING,
      withAuthJourneyProperties(journey, properties)
    );
  },

  // Sign-in success terminal, shared by BOTH surfaces (ENG-4330): the web
  // Auth0 redirect callback (auth/provider.tsx) and the Electron login IPC
  // handler (electron/api.ts) previously each built this event inline. One
  // helper means the two surfaces cannot drift apart on property shape, which
  // is what lets a single funnel read desktop and browser sign-in together
  // split only by the `client` envelope dimension.
  trackLoginSucceeded: (
    journey: AuthJourneyStateTransport,
    properties?: AuthClickProperties
  ): void => {
    track(
      EVENT_NAMES.LOGIN_SUCCEEDED,
      EVENT_CATEGORIES.AUTH,
      withAuthJourneyProperties(journey, properties)
    );
  },

  // Single browser writer for the signup terminal (SPEC T1 §3.3). Category
  // matches REGISTRATION_STARTED. Journey is optional: a first login can
  // complete after the journey TTL has expired.
  trackRegistrationCompleted: (
    journey?: AuthJourneyStateTransport,
    properties?: AuthClickProperties
  ): void => {
    track(
      EVENT_NAMES.REGISTRATION_COMPLETED,
      EVENT_CATEGORIES.ONBOARDING,
      journey ? withAuthJourneyProperties(journey, properties) : properties
    );
  },

  trackContinueWithEmailClicked: (
    journey: AuthJourneyStateTransport,
    properties?: AuthClickProperties
  ): void => {
    track(
      EVENT_NAMES.LOGIN_CONTINUE_WITH_EMAIL_CLICKED,
      EVENT_CATEGORIES.AUTH,
      withAuthJourneyProperties(journey, properties)
    );
  },

  // ENG-3451: Electron login screen's Google option. Mirrors
  // trackContinueWithEmailClicked exactly (same property shape, same journey
  // injection) so the two sign-in starts read as one funnel split by event
  // name — never merged, never drifted.
  trackContinueWithGoogleClicked: (
    journey: AuthJourneyStateTransport,
    properties?: AuthClickProperties
  ): void => {
    track(
      EVENT_NAMES.LOGIN_CONTINUE_WITH_GOOGLE_CLICKED,
      EVENT_CATEGORIES.AUTH,
      withAuthJourneyProperties(journey, properties)
    );
  },

  trackCreateAccountClicked: (
    journey: AuthJourneyStateTransport,
    properties?: AuthClickProperties
  ): void => {
    track(
      EVENT_NAMES.LOGIN_CREATE_ACCOUNT_CLICKED,
      EVENT_CATEGORIES.AUTH,
      withAuthJourneyProperties(journey, properties)
    );
  },

  // Fires when Auth0 redirect callback fails. Per ENG-3479 deferred
  // attribution: pass last_known_user_id from the call site so the event is
  // resolvable to a user even when the session is unauthenticated at fire time.
  // journey may be undefined when no active auth journey exists.
  // ENG-4348 stage 3: delegates to the shared dual-emit helper so this path
  // fires auth_login_failed AND legacy oauth_error like every other site.
  trackOAuthError: (
    journey: AuthJourneyStateTransport | undefined,
    properties: OAuthErrorProperties
  ): void => {
    trackAuthLoginFailed(track, journey, properties);
  }
});
